import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input, InputGroup, InputGroupAddon, InputGroupText, Spinner } from 'reactstrap';
import NotificationBanner from 'components/NotificationBanner';
import { fetchAddressSuggestions, fetchAddressDetails } from 'service/addressApi';

const DEFAULT_MIN_CHARS = 2;
const DEFAULT_DEBOUNCE_MS = 300;

const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = 'Start typing address...',
  disabled = false,
  minChars = DEFAULT_MIN_CHARS,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  className = '',
  onClear,
  maxLength,
  bsSize,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputRef = useRef(null);
  const listboxId = useRef(`addr-listbox-${Math.random().toString(36).slice(2)}`).current;
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Sync local value when parent value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
      if (!value) {
        setSuggestions([]);
        setIsOpen(false);
        setActiveIndex(-1);
        if (onClear) onClear();
      }
    }
  }, [value, inputValue, onClear]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const performSearch = useCallback(async (query) => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setErrorMsg('');
    try {
      setSuggestions(results);
      const results = await fetchAddressSuggestions(query, { signal: controller.signal });
      setIsOpen(true);
      setActiveIndex(results.length > 0 ? 0 : -1);
    } catch (error) {
      if (error.name !== 'AbortError') {
        setSuggestions([]);
        setErrorMsg('Error fetching addresses.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedSearch = useCallback((query) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (query.length >= minChars && !disabled) {
        performSearch(query);
      }
    }, debounceMs);
  }, [performSearch, minChars, debounceMs, disabled]);

  const handleChange = (e) => {
    let text = e.target.value;
    if (typeof maxLength === 'number') {
      text = text.slice(0, maxLength);
    }
    setInputValue(text);
    if (onChange) onChange(text);

    if (!text) {
      setSuggestions([]);
      setIsOpen(false);
      setActiveIndex(-1);
      if (onClear) onClear();
      return;
    }

    if (!disabled && text.length >= minChars) {
      debouncedSearch(text);
    } else {
      setSuggestions([]);
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  const handleSelect = async (item) => {
    setIsOpen(false);
    setSuggestions([]);
    setActiveIndex(-1);
    setInputValue(item.label);

    try {
      const details = await fetchAddressDetails(item.placeId);
      if (onSelect) onSelect({ placeId: item.placeId, label: item.label, details });
    } catch (error) {
      setErrorMsg('Failed to load address details.');
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' && suggestions.length > 0) {
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    if (e.key === 'Escape') {
      setIsOpen(false);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => {
        const next = prev + 1;
        return next >= suggestions.length ? 0 : next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => {
        const next = prev - 1;
        return next < 0 ? suggestions.length - 1 : next;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        handleSelect(suggestions[activeIndex]);
      }
    }
  };

  const handleBlur = () => {
    setTimeout(() => setIsOpen(false), 150);
  };

  const renderDropdown = () => {
    const showNoResults = !isLoading && inputValue.length >= minChars && suggestions.length === 0;
    if (!isOpen || disabled) return null;
    return (
      <div className="autocomplete-dropdown" role="listbox" id={listboxId} aria-multiselectable="false">
        <div className="dropdown-menu show" style={{ width: '100%', maxHeight: '220px', overflowY: 'auto' }}>
          {isLoading ? (
            <div className="dropdown-item text-center">
              <Spinner size="sm" /> Loading...
            </div>
          ) : showNoResults ? (
            <div className="dropdown-item text-muted">No results found</div>
          ) : (
            suggestions.map((s, idx) => (
              <div
                key={s.placeId}
                role="option"
                aria-selected={idx === activeIndex}
                className={`dropdown-item${idx === activeIndex ? ' active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(s)}
                style={{ cursor: 'pointer' }}
              >
                {s.label}
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`autocomplete-container ${className}`} aria-expanded={isOpen} aria-controls={listboxId}>
      <InputGroup>
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          bsSize={bsSize}
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={activeIndex >= 0 && suggestions[activeIndex] ? `opt-${suggestions[activeIndex].placeId}` : undefined}
        />
        {isLoading && (
          <InputGroupAddon addonType="append">
            <InputGroupText>
              <Spinner size="sm" />
            </InputGroupText>
          </InputGroupAddon>
        )}
      </InputGroup>
      {renderDropdown()}
      {!!errorMsg && (
        <NotificationBanner type="error" message={errorMsg} onClose={() => setErrorMsg('')} />
      )}
    </div>
  );
};

export default AddressAutocomplete;


